import axios from "axios";
import { Map, AdvancedMarker } from "@vis.gl/react-google-maps";
import React, { Fragment, useEffect, useState } from "react";
import { connect } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import BarLoader from "react-spinners/BarLoader";
import { setAlert } from "../../../../actions/alert";
import { getLatLong } from "../../../../util/geoUtils";
import MapMarker from "../../MapMarker";
import { ModalContent } from "../../../../styles/styled-components-utils";
import {
  LeftDiv,
  SpaceApartDiv,
} from "../restaurantBuilderLegacy/RestaurantCreateBuild";
import LocationPickerWidget from "./shared/LocationPickerWidget";
import { slugify } from "../../../../util/slugify";
import { useJobStatus } from "../../../../hooks/useJobStatus";
import { handleScraperError } from "../../../../utils/errorHandler";
import {
  getCreationEventContext,
  clearCreationEventContext,
} from "../../../../util/creationEventContext";
const RestaurantNameAddressLoc = ({ setAlert, token }) => {
  const [formData, setFormData] = useState({
    unsetGeoJson: { coordinates: [] },
    unsetAddress: "",
    status: "RESTAURANT",
  });
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [addressInput, setAddressInput] = useState("google");
  const [urlJobId, setUrlJobId] = useState(null);

  // Check if we have cuisine/veganOptions (came from RestaurantCuisine)
  const hasCuisineData = location.state && (
    location.state.cuisine ||
    location.state.veganOptions ||
    location.state.allMenuItems
  );

  useEffect(function syncFormFromLocationState() {
    if (location.state) {
      const { name, address, geoJson, status, website } = location.state;

      // Handle address: could be string (from scraper) or object with formatted_address
      const addressString = typeof address === 'string'
        ? address
        : address?.formatted_address || null;
      setFormData((oldData) => ({ ...oldData, name, address: addressString, geoJson, status, website }));

      // Pre-fill name from copied restaurant (fromAdHoc copy flow)
      const copiedName = location.state.restaurant?.name;
      if (copiedName && !name) {
        setFormData((old) => ({ ...old, name: copiedName }));
      }
    }
  }, [location.state]);

  // Use job status hook for URL processing when urlJobId is set
  // Only use this hook if we DON'T have cuisine data (meaning we're in the lookup/process flow)
  const {
    status: urlJobStatus,
    progress: urlJobProgress,
    progressMessage: urlJobProgressMessage,
    result: urlJobResult,
    error: urlJobError,
  } = useJobStatus(urlJobId && !hasCuisineData ? urlJobId : null, {
    onComplete: (result) => {
      setLoading(false);
      if (result && result.predictedOptions) {
        // Navigate to RestaurantCuisine with predictedOptions
        navigate("/create/v2/cuisine", { state: { ...location.state, ...result } });
      } else {
        // No predictedOptions, navigate to RestaurantCuisine with empty
        navigate("/create/v2/cuisine", { state: { ...location.state } });
      }
    },
    onError: (error) => {
      setLoading(false);
      setAlert(error || "An error occurred while processing.", "failure");
      // Only navigate to RestaurantCuisine if we don't have cuisine data
      // (meaning we were trying to lookup/process)
      if (!hasCuisineData) {
        navigate("/create/v2/cuisine", { state: { ...location.state } });
      }
    },
    autoConnect: !!urlJobId && !hasCuisineData,
  });
  const {
    name,
    address,
    geoJson,
    unsetGeoJson,
    searchAccepted,
    unsetAddress,
    status,
    website,
  } = formData;

  useEffect(function getGeolocationStartingPoint() {
    async function getStartingPoint() {
      if (!geoJson && navigator.geolocation) {
        await navigator.geolocation.getCurrentPosition(
          (position) => {
            setFormData((oldData) => ({
              ...oldData,
              unsetGeoJson: {
                coordinates: [
                  position.coords.longitude,
                  position.coords.latitude,
                ],
              },
            }));
          },
          (error) => {
            console.log(error);
          }
        );
      } else {
      }
    }
    getStartingPoint();
  }, [geoJson]);

  const createRestaurant = async () => {
    // Validate required fields
    if (!name || !name.trim()) {
      setAlert("Restaurant name is required", "failure");
      return;
    }

    if (!geoJson || !geoJson.coordinates || geoJson.coordinates.length !== 2) {
      setAlert("Restaurant location is required", "failure");
      return;
    }

    // Validate that at least one vegan option is selected
    const veganOptions = location.state.veganOptions || [];
    if (veganOptions.length === 0) {
      setAlert("Please select at least one vegan option before creating the restaurant", "failure");
      return;
    }

    const eventCtx = getCreationEventContext();
    const restaurantData = {
      name: name.trim(),
      location: `${geoJson.coordinates[1]},${geoJson.coordinates[0]}`,
      cuisine: location.state.cuisine || [],
      veganOptions: veganOptions,
      allMenuItems: location.state.allMenuItems || [],
      metadata: { isGoogle: false },
      status: status || "RESTAURANT",
      website: website || location.state?.website || null,
      googleId: location.state?.googleId || null,
      ...(address && {
        address: {
          formatted_address: address
        }
      }),
      ...(eventCtx?.eventId && { eventId: eventCtx.eventId, eventOnly: true }),
    };

    const config = {
      headers: {
        "Content-Type": "application/json",
      },
    };

    try {
      const response = await axios.post("/api/restaurants", JSON.stringify(restaurantData), config);
      console.log(response.data);
      setAlert("Restaurant successfully created", "success");
      if (eventCtx?.returnTo) {
        clearCreationEventContext();
        navigate(eventCtx.returnTo);
      } else {
        navigate(`/details/${response.data._id}/${slugify(response.data.name)}`);
      }
    } catch (err) {
      console.log(err.response);
      setAlert(
        err.response?.data?.errors
          ? err.response.data.errors[0].msg
          : "Error creating restaurant, please try again",
        "failure"
      );
    }
  };

  const confirmData = (e) => {
    e.preventDefault();

    // If we have cuisine data, create the restaurant (final step)
    if (hasCuisineData) {
      createRestaurant();
      return;
    }

    // If we don't have cuisine data and don't have geoJson yet, we need to get location first
    // This should not happen in normal flow, but handle it gracefully
    if (!geoJson || !geoJson.coordinates || geoJson.coordinates.length !== 2) {
      setAlert("Please provide a location before proceeding", "failure");
      return;
    }

    // Otherwise, navigate to RestaurantCuisine (original behavior - for manual entry flow)
    redirectToMenu(geoJson, status);
  };

  const redirectToMenu = (geoJson, status) => {
    // Only redirect to cuisine if we DON'T have cuisine data
    // (meaning we're in the manual entry flow, not the final step)
    if (!hasCuisineData) {
      navigate("/create/v2/cuisine", { state: { ...location.state, address, geoJson, status, name } });
    }
  };

  // Lookup restaurant by name and process if website found
  const lookupAndProcessRestaurant = async (restaurantName) => {
    if (!restaurantName || !restaurantName.trim()) return;

    setLoading(true);
    try {
      // Try to lookup restaurant using Google Places API
      // For now, we'll just navigate to RestaurantCuisine with empty predictedOptions
      // TODO: Implement actual lookup and processing
      navigate("/create/v2/cuisine", { state: { name: restaurantName.trim() } });
    } catch (error) {
      setLoading(false);
      handleScraperError(error, setAlert);
    }
  };

  // If we only have a name (from pencil flow), try to lookup and process
  useEffect(function lookupRestaurantByNameOnly() {
    if (location.state && location.state.name && !hasCuisineData && !geoJson && !urlJobId) {
      // We have just a name, try to lookup restaurant
      // For now, we'll skip lookup and just navigate to cuisine
      // This can be enhanced later to actually lookup and process
    }
  }, [location.state, hasCuisineData, geoJson, urlJobId]);

  const onChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const lookupAddress = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const [lng, lat] = unsetGeoJson.coordinates;
      const resp = await axios.get(
        `/api/restaurant_builder/address?lat=${lat}&lng=${lng}`
      );
      const address = resp.data;
      setFormData((old) => ({
        ...old,
        address,
        geoJson: { coordinates: [lng, lat] },
      }));
    } catch (e) {}
    setLoading(false);
  };

  const lookupLocation = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { lat, lng } = (await getLatLong(unsetAddress)).data;
      setFormData((old) => ({
        ...old,
        address: unsetAddress,
        geoJson: { coordinates: [lng, lat] },
      }));
    } catch (e) {
      setAlert(
        `${unsetAddress} not recognized as a valid address, please try a different input`,
        "failure"
      );
    }
    setLoading(false);
  };

  return (
    <Fragment>
      <div className="create-container">
        <form onSubmit={(e) => confirmData(e)}>
          <div className="form-container">
            <h2>Create a Restaurant</h2>
            <h3>Restaurant Name & Location</h3>
            <LeftDiv>
              <SpaceApartDiv>
                <label className="form-label-inline">Restaurant Name*</label>
                <input
                  type="text"
                  className="text-form"
                  name="name"
                  value={name}
                  onChange={onChange}
                  required
                />
              </SpaceApartDiv>
              {geoJson && geoJson.coordinates?.length === 2 && (
                <Fragment>
                  <SpaceApartDiv className="map-square">
                    <Map
                      center={{
                        lat: geoJson.coordinates[1],
                        lng: geoJson.coordinates[0],
                      }}
                      defaultZoom={12}
                      mapTypeControl={false}
                      streetViewControl={false}
                      mapId="ae90486c215209d65677867a"
                    >
                      <AdvancedMarker
                        position={{
                          lat: geoJson.coordinates[1],
                          lng: geoJson.coordinates[0],
                        }}
                      >
                        <MapMarker
                          restaurant={{ name, geoJson, cuisine: [] }}
                          text={
                            name?.length >= 16 ? name.slice(0, 14) + "..." : name
                          }
                          showLabel={false}
                        />
                      </AdvancedMarker>
                    </Map>
                    {/* </div> */}
                  </SpaceApartDiv>
                  {/* Only show address field if we have an address (from scraper) */}
                  {address && (
                    <SpaceApartDiv>
                      <label className="form-label-inline">Address</label>
                      <textarea
                        className="text-form"
                        name="address"
                        value={address}
                        onChange={onChange}
                      />
                    </SpaceApartDiv>
                  )}
                  <input
                    className="btn-primary btn-center"
                    type="submit"
                    value={hasCuisineData ? "Create Restaurant" : "Confirm & Proceed"}
                  />
                </Fragment>
              )}
              {!geoJson && !searchAccepted && (
                <SpaceApartDiv className="map-container-relative">
                  <div className="map-square">
                    <ModalContent className="text-left-margin">
                      No address found for {name}.
                      <hr />
                      <div>
                        <input
                          type="radio"
                          id="google"
                          name="getAddress"
                          value="google"
                          checked={addressInput === "google"}
                          onChange={(e) => setAddressInput("google")}
                        />
                        <label htmlFor="google">
                          Let VegifyIt look it up automatically{" "}
                          <span className="text-small">(easiest option)</span>
                        </label>
                      </div>
                      <hr />
                      <div>
                        <input
                          type="radio"
                          id="address"
                          name="getAddress"
                          value="address"
                          checked={addressInput === "address"}
                          onChange={(e) => setAddressInput("address")}
                        />
                        <label htmlFor="address">Type the address</label>
                      </div>
                      <hr />
                      <div>
                        <input
                          type="radio"
                          id="map"
                          name="getAddress"
                          value="map"
                          checked={addressInput === "map"}
                          onChange={(e) => setAddressInput("map")}
                        />
                        <label htmlFor="map">
                          Locate restaurant on map{" "}
                          <span className="text-small">
                            (helpful if you're at the restaurant now)
                          </span>
                        </label>
                      </div>
                      <hr />
                      <div>
                        <input
                          type="radio"
                          id="popup"
                          name="getAddress"
                          value="popup"
                          checked={addressInput === "popup"}
                          onChange={(e) => setAddressInput("popup")}
                        />
                        <label htmlFor="popup">
                          No permanent address{" "}
                          <span className="text-small">(popup/food truck)</span>
                        </label>
                      </div>
                      <button
                        type="button"
                        className="btn-primary"
                        onClick={async (e) => {
                          e.preventDefault();
                          switch (addressInput) {
                            case "map":
                              setFormData((data) => ({
                                ...data,
                                searchAccepted: "map",
                              }));
                              break;
                            case "address":
                              setFormData((data) => ({
                                ...data,
                                searchAccepted: "address",
                              }));
                              break;
                            case "popup": {
                              if (!e.target.form.reportValidity()) break;
                              const eventCtx = getCreationEventContext();
                              let popupGeoJson;
                              if (eventCtx?.eventId) {
                                setLoading(true);
                                try {
                                  const { data: event } = await axios.get(
                                    `/api/events/${eventCtx.eventId}`
                                  );
                                  popupGeoJson = {
                                    coordinates: [event.lng, event.lat],
                                  };
                                } catch (err) {
                                  setAlert(
                                    "Failed to load event location for popup",
                                    "failure"
                                  );
                                  setLoading(false);
                                  break;
                                }
                                setLoading(false);
                              } else if (
                                unsetGeoJson?.coordinates?.length === 2
                              ) {
                                popupGeoJson = unsetGeoJson;
                              } else {
                                setAlert(
                                  "Could not determine your current location. Please enable location services or pick a location on the map.",
                                  "failure"
                                );
                                break;
                              }
                              if (hasCuisineData) {
                                setFormData((data) => ({
                                  ...data,
                                  geoJson: popupGeoJson,
                                  status: "OFFLINE",
                                }));
                              } else {
                                redirectToMenu(popupGeoJson, "OFFLINE");
                              }
                              break;
                            }
                            case "google":
                              if (e.target.form.reportValidity()) {
                                navigate("/create/v2/address/google", {
                                  state: {
                                    ...location.state,
                                    name,
                                  },
                                });
                              }
                              break;
                            default:
                          }
                        }}
                      >
                        Go
                      </button>
                    </ModalContent>
                  </div>
                </SpaceApartDiv>
              )}
              {!geoJson && searchAccepted === "address" && (
                <Fragment>
                  <SpaceApartDiv>
                    <label className="form-label-inline">
                      Restaurant Address*
                    </label>
                    <textarea
                      value={unsetAddress}
                      onChange={(e) => {
                        const textValue = e.target.value;
                        setFormData((data) => ({
                          ...data,
                          unsetAddress: textValue,
                        }));
                      }}
                    ></textarea>
                    <input
                      className={`btn-primary btn-center ${
                        loading ? "loading" : ""
                      }`}
                      disabled={loading}
                      onClick={lookupLocation}
                      type="button"
                      value={"Submit"}
                    />
                  </SpaceApartDiv>
                </Fragment>
              )}

              {!geoJson && searchAccepted === "map" && (
                <LocationPickerWidget
                  unsetGeoJson={unsetGeoJson}
                  setFormData={setFormData}
                  isDisabled={loading}
                  name={name}
                  submitCallback={lookupAddress}
                />
              )}
            </LeftDiv>
          </div>
        </form>
        {loading && (
          <div className="loading-container-compact">
            <BarLoader
              loading={loading}
              color="var(--color-primary)"
              height="8px"
              cssOverride={{ margin: "0 auto", width: "100%" }}
              aria-label={
                searchAccepted === "map"
                  ? "Loading address from map"
                  : urlJobProgressMessage
                  ? urlJobProgressMessage
                  : "Loading address"
              }
            />
            {urlJobProgressMessage && (
              <div style={{ textAlign: "center", marginTop: "8px", fontSize: "14px" }}>
                {urlJobProgressMessage} {urlJobProgress > 0 && `(${urlJobProgress}%)`}
              </div>
            )}
          </div>
        )}
      </div>
    </Fragment>
  );
};
const mapStateToProps = (state) => ({
  currRestaurant: state.restaurantBuilder.restaurant,
  token: state.auth.token,
});
export default connect(mapStateToProps, { setAlert })(RestaurantNameAddressLoc);
